import { useState, useEffect } from 'react';
import { TopBar } from '../../components/feature/TopBar';
import { Sidebar } from '../../components/feature/Sidebar';

export default function EnergyPage() {
  const [meterData, setMeterData] = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [viewMode, setViewMode] = useState('overview');

  // Simular datos de la API Fronius Meter
  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      setTimeout(() => {
        const mockData = {
          EnergyReal_WAC_Sum_Produced: 45000 + Math.floor(Math.random() * 5000),
          EnergyReal_WAC_Sum_Consumed: 38000 + Math.floor(Math.random() * 4000),
          PowerReal_P_Sum: Math.floor(Math.random() * 2000) + 500,
          PowerReal_P_Phase_1: Math.floor(Math.random() * 700) + 200,
          PowerReal_P_Phase_2: Math.floor(Math.random() * 700) + 200,
          PowerReal_P_Phase_3: Math.floor(Math.random() * 700) + 200,
          Voltage_AC_Phase_1: 230 + Math.floor(Math.random() * 10) - 5,
          Voltage_AC_Phase_2: 230 + Math.floor(Math.random() * 10) - 5,
          Voltage_AC_Phase_3: 230 + Math.floor(Math.random() * 10) - 5,
          Current_AC_Phase_1: Math.floor(Math.random() * 10) + 2,
          Current_AC_Phase_2: Math.floor(Math.random() * 10) + 2,
          Current_AC_Phase_3: Math.floor(Math.random() * 10) + 2,
          Frequency_Phase_Average: 50 + Math.random() * 0.2 - 0.1,
          PowerFactor_Phase_1: 0.95 + Math.random() * 0.1 - 0.05,
          PowerFactor_Phase_2: 0.95 + Math.random() * 0.1 - 0.05,
          PowerFactor_Phase_3: 0.95 + Math.random() * 0.1 - 0.05
        };
        setMeterData(mockData);
        setLastUpdate(new Date());
        setIsLoading(false);
      }, 1000);
    };

    fetchData();
  }, [selectedPeriod]);

  const formatEnergy = (value?: number) => {
    if (value === undefined) return 'N/A';
    const kWh = value / 1000;
    return kWh.toLocaleString('es-ES', { maximumFractionDigits: 1 });
  };

  const formatPower = (value?: number) => {
    if (value === undefined) return 'N/A';
    return value.toLocaleString('es-ES');
  };



  const getPhaseStatus = (voltage?: number) => {
    if (!voltage) return 'unknown';
    if (voltage < 220 || voltage > 240) return 'warning';
    return 'normal';
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 ml-64">
          <TopBar title="Medidor de Energía" lastUpdate={lastUpdate} />
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando datos del medidor...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const energyBalance = (meterData?.EnergyReal_WAC_Sum_Produced || 0) - (meterData?.EnergyReal_WAC_Sum_Consumed || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-[var(--sidebar-width)]">
        <TopBar title="Medidor de Energía" lastUpdate={lastUpdate} />

        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-900 to-indigo-900 shadow-xl p-8">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558449028-b53a39d100fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center mix-blend-overlay"></div>
            </div>
            <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Medición Inteligente</h1>
                <p className="text-blue-100 max-w-xl">
                  Monitoreo preciso de consumo, producción y calidad de energía en tiempo real.
                </p>
              </div>
              <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-md p-2 rounded-xl border border-white/20">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="bg-transparent border-none text-white text-sm font-medium focus:ring-0 cursor-pointer"
                >
                  <option value="realtime" className="text-gray-900">Tiempo Real</option>
                  <option value="today" className="text-gray-900">Hoy</option>
                  <option value="week" className="text-gray-900">Esta Semana</option>
                  <option value="month" className="text-gray-900">Este Mes</option>
                </select>
                <div className="h-6 w-px bg-white/20"></div>
                <div className="flex bg-black/20 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('overview')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'overview'
                      ? 'bg-white text-blue-900 shadow-sm'
                      : 'text-white/70 hover:text-white'
                      }`}
                  >
                    Resumen
                  </button>
                  <button
                    onClick={() => setViewMode('phases')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'phases'
                      ? 'bg-white text-blue-900 shadow-sm'
                      : 'text-white/70 hover:text-white'
                      }`}
                  >
                    Por Fases
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Métricas Principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-sun-line text-green-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full border border-green-100">PRODUCCIÓN</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Energía Generada</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{formatEnergy(meterData?.EnergyReal_WAC_Sum_Produced)}</p>
                <p className="text-sm font-medium text-gray-400">kWh</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-home-line text-blue-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">CONSUMO</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Energía Consumida</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{formatEnergy(meterData?.EnergyReal_WAC_Sum_Consumed)}</p>
                <p className="text-sm font-medium text-gray-400">kWh</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-purple-50 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-flashlight-line text-purple-600 text-2xl"></i>
                </div>
                <span className="text-xs font-bold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">ACTUAL</span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Potencia Instantánea</h3>
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-bold text-gray-900">{formatPower(meterData?.PowerReal_P_Sum)}</p>
                <p className="text-sm font-medium text-gray-400">W</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform ${energyBalance >= 0 ? 'bg-teal-50' : 'bg-red-50'}`}>
                  <i className={`text-2xl ${energyBalance >= 0 ? 'ri-scales-3-line text-teal-600' : 'ri-scales-3-line text-red-600'}`}></i>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${energyBalance >= 0 ? 'text-teal-700 bg-teal-50 border-teal-100' : 'text-red-700 bg-red-50 border-red-100'}`}>
                  {energyBalance >= 0 ? 'SUPERÁVIT' : 'DÉFICIT'}
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Balance Neto</h3>
              <div className="flex items-baseline gap-1">
                <p className={`text-2xl font-bold ${energyBalance >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                  {formatEnergy(Math.abs(energyBalance))}
                </p>
                <p className="text-sm font-medium text-gray-400">kWh</p>
              </div>
            </div>
          </div>

          {viewMode === 'overview' ? (
            <>
              {/* Gráfico de Balance Energético */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Distribución Energética</h3>
                    <p className="text-sm text-gray-500">Análisis comparativo de producción vs consumo</p>
                  </div>
                  <div className="flex gap-2">
                    <span className="flex items-center text-xs font-medium text-gray-500">
                      <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span> Producción
                    </span>
                    <span className="flex items-center text-xs font-medium text-gray-500">
                      <span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span> Consumo
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  <div className="text-center group cursor-default">
                    <div className="relative w-40 h-40 mx-auto mb-6 transition-transform group-hover:scale-105 duration-300">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="3"
                          strokeDasharray={`${((meterData?.EnergyReal_WAC_Sum_Produced || 0) / 1000)}, 100`}
                          className="transition-all duration-1000 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="bg-green-50 rounded-full p-3 mb-1">
                          <i className="ri-sun-fill text-green-500 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-1">{formatEnergy(meterData?.EnergyReal_WAC_Sum_Produced)}</p>
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Energía Producida</p>
                  </div>

                  <div className="text-center group cursor-default">
                    <div className="relative w-40 h-40 mx-auto mb-6 transition-transform group-hover:scale-105 duration-300">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray={`${((meterData?.EnergyReal_WAC_Sum_Consumed || 0) / 1000)}, 100`}
                          className="transition-all duration-1000 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="bg-blue-50 rounded-full p-3 mb-1">
                          <i className="ri-home-fill text-blue-500 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-1">{formatEnergy(meterData?.EnergyReal_WAC_Sum_Consumed)}</p>
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Energía Consumida</p>
                  </div>

                  <div className="text-center group cursor-default">
                    <div className="relative w-40 h-40 mx-auto mb-6 transition-transform group-hover:scale-105 duration-300">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={energyBalance >= 0 ? "#10b981" : "#ef4444"}
                          strokeWidth="3"
                          strokeDasharray={`${Math.abs(energyBalance) / 1000}, 100`}
                          className="transition-all duration-1000 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className={`${energyBalance >= 0 ? 'bg-teal-50' : 'bg-red-50'} rounded-full p-3 mb-1`}>
                          <i className={`${energyBalance >= 0 ? 'ri-scales-3-fill text-teal-500' : 'ri-scales-3-fill text-red-500'} text-xl`}></i>
                        </div>
                      </div>
                    </div>
                    <p className={`text-2xl font-bold mb-1 ${energyBalance >= 0 ? 'text-teal-600' : 'text-red-600'}`}>
                      {formatEnergy(Math.abs(energyBalance))}
                    </p>
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Balance Neto</p>
                  </div>
                </div>
              </div>

              {/* Parámetros de Red */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Calidad de Red</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                        <i className="ri-flashlight-line text-xl"></i>
                      </div>
                      <span className="text-xs font-medium text-gray-400">W</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">Potencia Total</p>
                    <p className="text-2xl font-bold text-gray-900">{formatPower(meterData?.PowerReal_P_Sum)}</p>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-purple-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
                        <i className="ri-pulse-line text-xl"></i>
                      </div>
                      <span className="text-xs font-medium text-gray-400">Hz</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">Frecuencia</p>
                    <p className="text-2xl font-bold text-gray-900">{meterData?.Frequency_Phase_Average?.toFixed(2) || 'N/A'}</p>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-green-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-green-100 p-2 rounded-lg text-green-600">
                        <i className="ri-battery-line text-xl"></i>
                      </div>
                      <span className="text-xs font-medium text-gray-400">V</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">Voltaje Promedio</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {((meterData?.Voltage_AC_Phase_1 + meterData?.Voltage_AC_Phase_2 + meterData?.Voltage_AC_Phase_3) / 3).toFixed(1) || 'N/A'}
                    </p>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-orange-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-orange-100 p-2 rounded-lg text-orange-600">
                        <i className="ri-pie-chart-line text-xl"></i>
                      </div>
                      <span className="text-xs font-medium text-gray-400">PF</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">Factor de Potencia</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {((meterData?.PowerFactor_Phase_1 + meterData?.PowerFactor_Phase_2 + meterData?.PowerFactor_Phase_3) / 3).toFixed(3) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Vista por Fases */
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Detalle por Fases</h3>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((phase) => {
                    const voltage = meterData?.[`Voltage_AC_Phase_${phase}`];
                    const current = meterData?.[`Current_AC_Phase_${phase}`];
                    const power = meterData?.[`PowerReal_P_Phase_${phase}`];
                    const powerFactor = meterData?.[`PowerFactor_Phase_${phase}`];
                    const phaseStatus = getPhaseStatus(voltage);

                    return (
                      <div key={phase} className={`p-6 rounded-2xl border transition-all hover:shadow-md ${phaseStatus === 'warning' ? 'border-yellow-200 bg-yellow-50/30' : 'border-gray-100 bg-gray-50/30'
                        }`}>
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold ${phase === 1 ? 'bg-blue-500' : phase === 2 ? 'bg-green-500' : 'bg-purple-500'
                              }`}>
                              L{phase}
                            </div>
                            <h4 className="text-lg font-bold text-gray-900">Fase {phase}</h4>
                          </div>
                          {phaseStatus === 'warning' && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-1 rounded-full flex items-center">
                              <i className="ri-alert-line mr-1"></i> Alerta
                            </span>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded-xl border border-gray-100">
                              <p className="text-xs font-medium text-gray-500 mb-1">Voltaje</p>
                              <div className="flex items-baseline gap-1">
                                <p className={`text-lg font-bold ${phaseStatus === 'warning' ? 'text-yellow-600' : 'text-gray-900'}`}>
                                  {voltage || 'N/A'}
                                </p>
                                <span className="text-xs text-gray-400">V</span>
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-100">
                              <p className="text-xs font-medium text-gray-500 mb-1">Corriente</p>
                              <div className="flex items-baseline gap-1">
                                <p className="text-lg font-bold text-gray-900">{current || 'N/A'}</p>
                                <span className="text-xs text-gray-400">A</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white p-3 rounded-xl border border-gray-100">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Potencia Real</p>
                                <div className="flex items-baseline gap-1">
                                  <p className="text-lg font-bold text-gray-900">{formatPower(power)}</p>
                                  <span className="text-xs text-gray-400">W</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-medium text-gray-500 mb-1">Factor Potencia</p>
                                <p className="text-lg font-bold text-gray-900">{powerFactor?.toFixed(3) || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Comparativa de Fases */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Balance de Cargas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-bar-chart-horizontal-line mr-2 text-gray-400"></i>
                      Distribución de Potencia
                    </h4>
                    <div className="space-y-4">
                      {[1, 2, 3].map((phase) => {
                        const power = meterData?.[`PowerReal_P_Phase_${phase}`] || 0;
                        const maxPower = Math.max(
                          meterData?.PowerReal_P_Phase_1 || 0,
                          meterData?.PowerReal_P_Phase_2 || 0,
                          meterData?.PowerReal_P_Phase_3 || 0
                        );
                        const percentage = maxPower > 0 ? (power / maxPower) * 100 : 0;

                        return (
                          <div key={phase}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-600">Fase {phase}</span>
                              <span className="text-sm font-bold text-gray-900">{formatPower(power)} W</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                              <div
                                className={`h-2.5 rounded-full transition-all duration-500 ${phase === 1 ? 'bg-blue-500' :
                                  phase === 2 ? 'bg-green-500' : 'bg-purple-500'
                                  }`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-scales-line mr-2 text-gray-400"></i>
                      Estado del Balance
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map((phase) => {
                        const voltage = meterData?.[`Voltage_AC_Phase_${phase}`];
                        const isBalanced = voltage && voltage >= 220 && voltage <= 240;

                        return (
                          <div key={phase} className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                            <div className={`w-3 h-3 rounded-full mx-auto mb-3 ${isBalanced ? 'bg-green-500 shadow-lg shadow-green-500/30' : 'bg-yellow-500 shadow-lg shadow-yellow-500/30 animate-pulse'
                              }`}></div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Fase {phase}</p>
                            <p className={`text-sm font-bold ${isBalanced ? 'text-green-600' : 'text-yellow-600'
                              }`}>
                              {isBalanced ? 'OK' : 'Revisar'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}